import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CffAsyncTypeahead, type CffAsyncOption } from '../../components/forms/CffAsyncTypeahead';
import { IntlPhoneField } from '../../components/forms/IntlPhoneField';
import { sgPhoneToE164 } from '../../components/forms/SgPhoneField';
import { buildDeliveryMapStops, DeliveryRouteMap } from '../../components/dem/DeliveryRouteMap';
import { LalamoveVehiclePicker } from '../../components/dem/LalamoveVehiclePicker';
import { LalamoveSpecialRequestsPicker } from '../../components/dem/LalamoveSpecialRequestsPicker';
import { LalamoveOrderViewModal } from '../../components/dem/LalamoveOrderViewModal';
import { DemTypeahead } from '../../components/dem/DemTypeahead';
import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useDemNotifications } from '../../contexts/DemNotificationsContext';
import { usePortal } from '../../contexts/PortalContext';
import {
  demMainCustomerCode,
  composeDeliveryAddress,
  extractPostalFromAddress,
  fetchDemCustomers,
  formatDemCustomerAddressDisplay,
} from '../../lib/demCustomers';
import { fetchPostalCoords } from '../../lib/demDispatch';
import { resolveSingaporePostalLookup } from '../../lib/demDispatchGeocode';
import {
  confirmMultiStopDelivery,
  reorderCancelledDelivery,
  crmMatchWarning,
  fetchLalamoveLiveOrder,
  fetchLalamoveServiceTypes,
  fetchMultiStopDeliveryQuote,
  fetchDeliveryBatch,
  isDeliveryStatusCancelled,
  formatDeliveryMoney,
  formatLalamovePhoneError,
  isDeliveryQuotationExpired,
  type DeliveryGeocodedStops,
  type DeliveryQuotationPreview,
  LALAMOVE_SG_VEHICLE_FALLBACK,
  friendlyLalamoveVehicleName,
  resolveCrmClientFromDemCode,
  type LalamoveVehicleOption,
  type LalamoveSpecialRequestOption,
  type LalamoveLiveOrder,
} from '../../lib/deliveryArrangements';
import {
  applyDriverNotesTemplate,
  DEFAULT_DRIVER_NOTES_TEMPLATE,
  fetchDeliveryFormSettings,
} from '../../lib/deliveryFormSettings';
import { parseLalamoveOrderReference } from '../../lib/lalamoveOrderReference';
import { arrangementBatchToNewFormPrefill } from '../../lib/deliveryArrangementPrefill';
import { fetchUnifiedPortalUsers } from '../../lib/portalUnifiedUsers';
import { normalizeDeliveryPhone } from '../../lib/intlPhone';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import { fetchDeliveryWarehouseAddresses } from '../../lib/deliverySettings';
import type { DemWarehouseAddress } from '../../types/dem';
import type { DeliveryFulfillmentType, InternalAssigneeType } from '../../types/delivery';

type WizardStep = 1 | 2 | 3;

type AddressParts = {
  address: string;
  unitNo: string;
  building: string;
  postal: string;
};

type StopForm = AddressParts & {
  name: string;
  phone: string;
};

type DropoffStopForm = AddressParts & {
  key: string;
  demCustomerCode: string;
  demCustomerName: string;
  crmClientId: string | null;
  crmClientCode: string;
  crmMatched: boolean;
  name: string;
  phone: string;
  notesToDriver: string;
  reason: string;
  documentNo: string;
};

const EMPTY_ADDRESS: AddressParts = { address: '', unitNo: '', building: '', postal: '' };
const EMPTY_PICKUP: StopForm = { name: '', phone: '', ...EMPTY_ADDRESS };

const WIZARD_STEPS: { step: WizardStep; label: string; caption: string }[] = [
  { step: 1, label: 'Pickup', caption: 'Type, vehicle & pickup' },
  { step: 2, label: 'Dropoff', caption: 'Stops & internal refs' },
  { step: 3, label: 'Review', caption: 'Quote & confirm' },
];

function newDropoffStop(): DropoffStopForm {
  return {
    key: crypto.randomUUID(),
    demCustomerCode: '',
    demCustomerName: '',
    crmClientId: null,
    crmClientCode: '',
    crmMatched: false,
    name: '',
    phone: '',
    ...EMPTY_ADDRESS,
    notesToDriver: '',
    reason: '',
    documentNo: '',
  };
}

function composedAddress(parts: AddressParts): string {
  return composeDeliveryAddress(parts);
}

async function geocodeStop(stop: AddressParts): Promise<{ lat: number; lng: number; routeAddress: string }> {
  const postal = extractPostalFromAddress(stop.address, stop.postal);
  if (!postal) {
    throw new Error('Enter a valid 6-digit Singapore postal code for pickup and each dropoff.');
  }
  const lookup = await resolveSingaporePostalLookup(postal, async (code) => {
    const cache = await fetchPostalCoords([code]);
    const hit = cache.get(code);
    return hit ? { lat: hit[0], lng: hit[1] } : null;
  });
  if (!lookup) {
    throw new Error(`Could not geocode postal code ${postal}. Check the postal code and try again.`);
  }
  return {
    lat: lookup.lat,
    lng: lookup.lng,
    routeAddress: composedAddress(stop),
  };
}

function DeliveryFormCollapse({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className={`delivery-form-collapse${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="delivery-form-collapse__toggle"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span>{title}</span>
        <span aria-hidden>{open ? '▾' : '▸'}</span>
      </button>
      {open ? <div className="delivery-form-collapse__body">{children}</div> : null}
    </section>
  );
}

export function DemDeliveriesNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reorderFromId = searchParams.get('reorder')?.trim() ?? '';
  const { basePath } = usePortal();
  const { push: pushToast } = useDemNotifications();
  const { profile, session } = useAuth();
  const listBase = basePath ? `${basePath}/deliveries` : '/deliveries';
  const settingsPath = basePath ? `${basePath}/settings` : '/settings';

  const [step, setStep] = useState<WizardStep>(1);
  const [salesUsers, setSalesUsers] = useState<{ value: string; label: string }[]>([]);
  const [warehouses, setWarehouses] = useState<DemWarehouseAddress[]>([]);
  const [warehouseModalOpen, setWarehouseModalOpen] = useState(false);

  const [fulfillmentType, setFulfillmentType] = useState<DeliveryFulfillmentType>('lalamove');
  const [internalType, setInternalType] = useState<InternalAssigneeType>('driver');
  const [internalAssigneeName, setInternalAssigneeName] = useState('');
  const [requestedBy, setRequestedBy] = useState('');
  const [issueCausedBy, setIssueCausedBy] = useState('');
  const [vehicleType, setVehicleType] = useState('MOTORCYCLE');
  const [vehicleOptions, setVehicleOptions] = useState<LalamoveVehicleOption[]>(
    LALAMOVE_SG_VEHICLE_FALLBACK,
  );
  const [vehicleOptionsLoading, setVehicleOptionsLoading] = useState(true);
  const [pickup, setPickup] = useState<StopForm>({ ...EMPTY_PICKUP });
  const [dropoffs, setDropoffs] = useState<DropoffStopForm[]>([newDropoffStop()]);

  const [saving, setSaving] = useState(false);
  const [quotePreview, setQuotePreview] = useState<DeliveryQuotationPreview | null>(null);
  const [geocodedStops, setGeocodedStops] = useState<DeliveryGeocodedStops | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteSilentRefreshing, setQuoteSilentRefreshing] = useState(false);
  const [quoteRefreshError, setQuoteRefreshError] = useState<string | null>(null);

  const [driverNotesTemplate, setDriverNotesTemplate] = useState(DEFAULT_DRIVER_NOTES_TEMPLATE);
  const [specialRequests, setSpecialRequests] = useState<string[]>([]);
  const [orderIdInput, setOrderIdInput] = useState('');
  const [orderLoadBusy, setOrderLoadBusy] = useState(false);
  const [orderViewBusy, setOrderViewBusy] = useState(false);
  const [loadedLalamoveOrder, setLoadedLalamoveOrder] = useState<LalamoveLiveOrder | null>(null);
  const [viewLalamoveOrder, setViewLalamoveOrder] = useState<LalamoveLiveOrder | null>(null);
  const [orderViewOpen, setOrderViewOpen] = useState(false);
  const [reorderPrefillLoading, setReorderPrefillLoading] = useState(Boolean(reorderFromId));
  const [arrangementOpen, setArrangementOpen] = useState(true);
  const [attributionOpen, setAttributionOpen] = useState(true);
  const [routeOpen, setRouteOpen] = useState(true);
  const [closedDropoffKeys, setClosedDropoffKeys] = useState<Set<string>>(() => new Set());

  const creator = profile?.display_name?.trim() || profile?.username || 'unknown';

  const notifyFormError = useCallback(
    (message: string, title = "Can't continue") => {
      const trimmed = message.trim();
      if (!trimmed) return;
      pushToast(pageFeedbackToToast({ kind: 'error', message: trimmed, title }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [pushToast],
  );

  useEffect(() => {
    void (async () => {
      try {
        const [userRows, warehouseRows, formSettings] = await Promise.all([
          fetchUnifiedPortalUsers().catch(() => []),
          fetchDeliveryWarehouseAddresses().catch(() => []),
          fetchDeliveryFormSettings().catch(() => null),
        ]);
        if (formSettings) {
          setDriverNotesTemplate(formSettings.driverNotesTemplate);
          setSpecialRequests(formSettings.defaultSpecialRequests);
        }
        setSalesUsers(
          userRows
            .map((user) => {
              const name = user.displayName.trim();
              const email = user.email.trim();
              const value = name || email;
              const label = name && email && name !== email ? `${name} · ${email}` : value;
              return { value, label };
            })
            .filter((user) => user.value)
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
        );
        setWarehouses(warehouseRows);
        setVehicleOptionsLoading(true);
        fetchLalamoveServiceTypes()
          .then((options) => {
            setVehicleOptions(options);
            setVehicleType((current) =>
              options.some((opt) => opt.value === current) ? current : (options[0]?.value ?? 'MOTORCYCLE'),
            );
          })
          .catch(() => setVehicleOptions(LALAMOVE_SG_VEHICLE_FALLBACK))
          .finally(() => setVehicleOptionsLoading(false));
      } catch (err) {
        notifyFormError(
          err instanceof Error ? err.message : 'Failed to load form data.',
          'Could not load delivery form',
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (!reorderFromId) return;

    let cancelled = false;
    void (async () => {
      setReorderPrefillLoading(true);
      try {
        const batch = await fetchDeliveryBatch(reorderFromId);
        if (cancelled) return;

        if (!batch.every((row) => isDeliveryStatusCancelled(row.status))) {
          notifyFormError('Only cancelled deliveries can be reordered.', 'Reorder not available');
          return;
        }

        const prefill = arrangementBatchToNewFormPrefill(batch);
        if (!prefill) {
          notifyFormError('Could not load the previous arrangement.', 'Reorder failed');
          return;
        }

        setFulfillmentType(prefill.fulfillmentType);
        setVehicleType(prefill.vehicleType);
        setPickup({ ...prefill.pickup });
        setRequestedBy(prefill.requestedBy);
        setIssueCausedBy(prefill.issueCausedBy);

        // Resolve account codes before committing dropoffs so Account typeahead is filled
        // and we don't race React state (setDropoffs then async update can wipe prefill).
        const nextDropoffs = await Promise.all(
          prefill.dropoffs.map(async (stop) => {
            const code = (stop.demCustomerCode.trim() || stop.crmClientCode.trim()).trim();
            if (!code) {
              return { ...stop, key: crypto.randomUUID() };
            }
            const [crm, demLookup] = await Promise.all([
              resolveCrmClientFromDemCode(code).catch(() => null),
              fetchDemCustomers({
                search: code,
                pageSize: 20,
                blockedFilter: 'unblocked',
              }).catch(() => null),
            ]);
            const match =
              demLookup?.rows.find((row) => row.code.trim().toUpperCase() === code.toUpperCase()) ??
              demLookup?.rows.find(
                (row) =>
                  demMainCustomerCode(row.code).toUpperCase() ===
                  demMainCustomerCode(code).toUpperCase(),
              ) ??
              null;
            return {
              ...stop,
              key: crypto.randomUUID(),
              demCustomerCode: code,
              demCustomerName:
                match?.name?.trim() ||
                crm?.company_name?.trim() ||
                stop.demCustomerName ||
                '',
              crmClientId: crm?.id ?? stop.crmClientId,
              crmClientCode: crm?.code ?? (demMainCustomerCode(code) || stop.crmClientCode),
              crmMatched: Boolean(crm) || stop.crmMatched,
            };
          }),
        );
        if (cancelled) return;

        setDropoffs(nextDropoffs);
        if (prefill.fulfillmentType === 'internal') {
          setInternalType(prefill.internalType);
          setInternalAssigneeName(prefill.internalAssigneeName);
        }
        setStep(2);

        pushToast(
          pageFeedbackToToast({
            kind: 'success',
            title: 'Cancelled arrangement loaded',
            message:
              'Pickup, dropoffs and account codes are filled in. Review details, get a new quote, then confirm.',
          }),
        );
      } catch (err) {
        if (!cancelled) {
          notifyFormError(
            err instanceof Error ? err.message : 'Could not load arrangement for reorder.',
            'Reorder failed',
          );
        }
      } finally {
        if (!cancelled) setReorderPrefillLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reorderFromId, notifyFormError, pushToast]);

  const salesOptions = useMemo(
    () => salesUsers.map((user) => ({ value: user.value, label: user.label })),
    [salesUsers],
  );

  const issueCausedByOptions = useMemo(
    () => [
      { value: 'Warehouse', label: 'Warehouse' },
      { value: 'Customer', label: 'Customer' },
      { value: 'Purchasing', label: 'Purchasing' },
      ...salesOptions,
    ],
    [salesOptions],
  );

  const availableSpecialRequests = useMemo((): LalamoveSpecialRequestOption[] => {
    return vehicleOptions.find((option) => option.value === vehicleType)?.specialRequests ?? [];
  }, [vehicleOptions, vehicleType]);

  useEffect(() => {
    const allowed = new Set(availableSpecialRequests.map((option) => option.name));
    setSpecialRequests((prev) => {
      const next = prev.filter((name) => allowed.has(name));
      return next.length === prev.length ? prev : next;
    });
  }, [availableSpecialRequests]);

  const routeQuoteSignature = useMemo(
    () =>
      JSON.stringify({
        fulfillmentType,
        vehicleType,
        pickup,
        dropoffs: dropoffs.map(({ notesToDriver: _notes, ...rest }) => rest),
      }),
    [fulfillmentType, vehicleType, pickup, dropoffs],
  );

  const specialRequestsSignature = useMemo(
    () => JSON.stringify([...specialRequests].sort()),
    [specialRequests],
  );

  useEffect(() => {
    setQuotePreview(null);
    setGeocodedStops(null);
    setQuoteRefreshError(null);
  }, [routeQuoteSignature]);

  const prevSpecialRequestsRef = useRef(specialRequestsSignature);

  const quoteExpired = isDeliveryQuotationExpired(quotePreview);
  const hasValidQuote = Boolean(quotePreview && !quoteExpired);

  useEffect(() => {
    if (!quotePreview?.expiresAt) return;
    const expiresAtMs = new Date(quotePreview.expiresAt).getTime();
    const delay = expiresAtMs - Date.now();
    if (delay <= 0) {
      setQuotePreview(null);
      setGeocodedStops(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setQuotePreview(null);
      setGeocodedStops(null);
    }, delay + 50);
    return () => window.clearTimeout(timer);
  }, [quotePreview]);

  const mapStops = useMemo(
    () =>
      buildDeliveryMapStops(
        {
          address: composedAddress(pickup),
          postal: pickup.postal,
          name: pickup.name,
        },
        dropoffs.map((stop) => ({
          key: stop.key,
          address: composedAddress(stop),
          postal: stop.postal,
          name: stop.name,
          demCustomerCode: stop.demCustomerCode,
        })),
      ),
    [pickup, dropoffs],
  );

  const vehicleLabel =
    vehicleOptions.find((opt) => opt.value === vehicleType)?.label ??
    friendlyLalamoveVehicleName(vehicleType);

  const crmUnmatchedCount = dropoffs.filter(
    (stop) => stop.demCustomerCode.trim() && !stop.crmMatched,
  ).length;

  const searchDemCustomers = async (query: string): Promise<CffAsyncOption[]> => {
    const result = await fetchDemCustomers({ search: query, pageSize: 50, blockedFilter: 'unblocked' });
    return result.rows.map((customer) => ({
      value: customer.code,
      label: `${customer.name} (${customer.code})`,
      meta: customer,
    }));
  };

  const applyDemCustomer = async (index: number, code: string, meta?: unknown) => {
    const customer = meta as
      | { name?: string; address?: string; address_2?: string; postal?: string }
      | undefined;
    const crm = code ? await resolveCrmClientFromDemCode(code).catch(() => null) : null;
    const mainCode = demMainCustomerCode(code);
    const address = customer
      ? formatDemCustomerAddressDisplay(customer)
      : '';
    const postal = extractPostalFromAddress(address, customer?.postal ?? '');

    setDropoffs((prev) =>
      prev.map((stop, stopIndex) =>
        stopIndex === index
          ? {
              ...stop,
              demCustomerCode: code,
              demCustomerName: String(customer?.name ?? stop.demCustomerName),
              crmClientId: crm?.id ?? null,
              crmClientCode: crm?.code ?? mainCode,
              crmMatched: Boolean(crm),
              address,
              postal: postal || stop.postal,
            }
          : stop,
      ),
    );
  };

  const updateDropoff = (index: number, patch: Partial<DropoffStopForm>) => {
    setDropoffs((prev) => prev.map((stop, stopIndex) => (stopIndex === index ? { ...stop, ...patch } : stop)));
  };

  const applyLalamoveOrderToForm = (order: LalamoveLiveOrder) => {
    if (order.stops.length < 2) {
      throw new Error('This Lalamove order has no dropoff stop to load.');
    }

    const [pickupStop, ...dropoffStops] = order.stops;
    const pickupAddress = pickupStop.address.trim();
    const template = applyDriverNotesTemplate(driverNotesTemplate);

    setPickup({
      name: '',
      phone: pickupStop.phone ? sgPhoneToE164(pickupStop.phone) : '',
      address: pickupAddress,
      unitNo: '',
      building: '',
      postal: extractPostalFromAddress(pickupAddress, ''),
    });

    setDropoffs(
      dropoffStops.map((stop) => {
        const address = stop.address.trim();
        return {
          ...newDropoffStop(),
          key: crypto.randomUUID(),
          name: '',
          phone: stop.phone ? sgPhoneToE164(stop.phone) : '',
          address,
          unitNo: '',
          building: '',
          postal: extractPostalFromAddress(address, ''),
          documentNo: order.orderId,
          reason: '',
          notesToDriver: template,
        };
      }),
    );
    setFulfillmentType('lalamove');
  };

  const resolveLalamoveOrderInput = (): string => {
    const orderId = parseLalamoveOrderReference(orderIdInput);
    if (!orderId) {
      throw new Error('Enter a Lalamove order ID or paste a tracking link.');
    }
    return orderId;
  };

  const handleLoadLalamoveOrder = async () => {
    setOrderLoadBusy(true);
    try {
      const orderId = resolveLalamoveOrderInput();
      const order =
        loadedLalamoveOrder?.orderId === orderId
          ? loadedLalamoveOrder
          : await fetchLalamoveLiveOrder(orderId);
      setLoadedLalamoveOrder(order);
      applyLalamoveOrderToForm(order);
      pushToast(
        pageFeedbackToToast({
          kind: 'success',
          title: 'Lalamove order loaded',
          message: `${order.orderId} applied to pickup and dropoff stops.`,
        }),
      );
    } catch (err) {
      notifyFormError(
        err instanceof Error ? err.message : 'Could not load Lalamove order.',
        'Load failed',
      );
    } finally {
      setOrderLoadBusy(false);
    }
  };

  const handleViewLalamoveOrder = async () => {
    setOrderViewBusy(true);
    try {
      const orderId = resolveLalamoveOrderInput();
      const order =
        loadedLalamoveOrder?.orderId === orderId
          ? loadedLalamoveOrder
          : await fetchLalamoveLiveOrder(orderId);
      setLoadedLalamoveOrder(order);
      setViewLalamoveOrder(order);
      setOrderViewOpen(true);
    } catch (err) {
      notifyFormError(
        err instanceof Error ? err.message : 'Could not load Lalamove order.',
        'View failed',
      );
    } finally {
      setOrderViewBusy(false);
    }
  };

  const prefillDriverNotes = () => {
    const template = applyDriverNotesTemplate(driverNotesTemplate);
    setDropoffs((prev) =>
      prev.map((stop) => ({
        ...stop,
        notesToDriver: stop.notesToDriver.trim() ? stop.notesToDriver : template,
      })),
    );
  };

  const applyWarehouse = (warehouse: DemWarehouseAddress) => {
    setPickup({
      name: '',
      phone: warehouse.phone ? sgPhoneToE164(warehouse.phone) : '',
      address: warehouse.address,
      unitNo: warehouse.unitNo ?? '',
      building: warehouse.building ?? '',
      postal: warehouse.postal || extractPostalFromAddress(warehouse.address, ''),
    });
    setWarehouseModalOpen(false);
  };

  const applyRoutePostalToPickup = async (rawPostal: string) => {
    const postal = rawPostal.replace(/\D/g, '').slice(0, 6);
    setPickup((prev) => ({ ...prev, postal }));
    if (postal.length !== 6) return;
    const hit = await resolveSingaporePostalLookup(postal, async (code) => {
      const cache = await fetchPostalCoords([code]);
      const cached = cache.get(code);
      return cached ? { lat: cached[0], lng: cached[1] } : null;
    });
    if (!hit) return;
    setPickup((prev) => ({
      ...prev,
      postal: hit.postal,
      address: prev.address.trim() ? prev.address : hit.address,
    }));
  };

  const applyRoutePostalToDropoff = async (index: number, rawPostal: string) => {
    const postal = rawPostal.replace(/\D/g, '').slice(0, 6);
    updateDropoff(index, { postal });
    if (postal.length !== 6) return;
    const hit = await resolveSingaporePostalLookup(postal, async (code) => {
      const cache = await fetchPostalCoords([code]);
      const cached = cache.get(code);
      return cached ? { lat: cached[0], lng: cached[1] } : null;
    });
    if (!hit) return;
    setDropoffs((prev) =>
      prev.map((stop, stopIndex) =>
        stopIndex === index
          ? {
              ...stop,
              postal: hit.postal,
              address: stop.address.trim() ? stop.address : hit.address,
            }
          : stop,
      ),
    );
  };

  function validateStep1(): string | null {
    if (!requestedBy.trim()) return 'Select who requested this delivery.';
    if (!issueCausedBy.trim()) return 'Select who/what caused the issue.';
    if (fulfillmentType === 'internal' && internalType === 'sales' && !internalAssigneeName.trim()) {
      return 'Select a sales account from User Management.';
    }
    if (!vehicleType.trim()) return 'Select a vehicle type.';
    if (!pickup.address.trim()) return 'Pickup address is required.';
    if (!/^\d{6}$/.test(pickup.postal.trim())) return 'Pickup postal code is required (6 digits).';
    if (fulfillmentType === 'lalamove') {
      if (!pickup.name.trim()) return 'Pickup name is required for Lalamove delivery.';
      if (!normalizeDeliveryPhone(pickup.phone)) return formatLalamovePhoneError('Pickup phone');
    }
    return null;
  }

  function validateStep2(): string | null {
    for (let index = 0; index < dropoffs.length; index += 1) {
      const stop = dropoffs[index]!;
      if (!stop.demCustomerCode.trim()) return `Select an account for dropoff ${index + 1}.`;
      if (!stop.address.trim()) return `Dropoff ${index + 1} address is required.`;
      if (!/^\d{6}$/.test(stop.postal.trim())) {
        return `Dropoff ${index + 1} postal code is required (6 digits).`;
      }
      if (!stop.reason.trim()) return `Reason is required for dropoff ${index + 1}.`;
      if (fulfillmentType === 'lalamove') {
        if (!stop.name.trim()) return `Dropoff ${index + 1} name is required.`;
        const phone = normalizeDeliveryPhone(stop.phone);
        if (!phone) return formatLalamovePhoneError(`Dropoff ${index + 1} phone`);
        if (phone === normalizeDeliveryPhone(pickup.phone)) {
          return `Dropoff ${index + 1} phone must be different from pickup phone.`;
        }
      }
    }
    return null;
  }

  function validateLalamoveContacts(): string | null {
    if (fulfillmentType !== 'lalamove') return null;
    return validateStep1() ?? validateStep2();
  }

  function buildConfirmInput() {
    return {
      fulfillmentType,
      pickup: {
        name: pickup.name.trim(),
        phone: pickup.phone.trim(),
        address: pickup.address.trim(),
        unitNo: pickup.unitNo.trim() || undefined,
        building: pickup.building.trim() || undefined,
        postal: pickup.postal.trim() || undefined,
      },
      stops: dropoffs.map((stop) => ({
        demCustomerCode: stop.demCustomerCode.trim(),
        clientId: stop.crmClientId,
        clientName: stop.demCustomerName.trim() || stop.demCustomerCode.trim(),
        clientCode: stop.crmClientCode.trim() || demMainCustomerCode(stop.demCustomerCode),
        dropoff: {
          name: stop.name.trim(),
          phone: stop.phone.trim(),
          address: stop.address.trim(),
          unitNo: stop.unitNo.trim() || undefined,
          building: stop.building.trim() || undefined,
          postal: stop.postal.trim() || undefined,
          notesToDriver: stop.notesToDriver.trim() || undefined,
        },
        reason: stop.reason.trim(),
        documentNo: stop.documentNo.trim(),
        notesToDriver: stop.notesToDriver.trim(),
      })),
      vehicleType,
      specialRequests,
      createdBy: creator,
      createdByAuthId: profile?.id || session?.user?.id || null,
      internalAssigneeType: fulfillmentType === 'internal' ? internalType : undefined,
      internalAssigneeName:
        fulfillmentType === 'internal'
          ? internalType === 'driver'
            ? 'Driver'
            : internalAssigneeName.trim()
          : undefined,
      requestedBy: requestedBy.trim(),
      issueCausedBy: issueCausedBy.trim(),
    };
  }

  async function buildGeocodedStops(): Promise<DeliveryGeocodedStops> {
    const pickupGeo = await geocodeStop(pickup);
    const geocodedDropoffs = await Promise.all(
      dropoffs.map(async (stop) => {
        const geo = await geocodeStop(stop);
        return {
          name: stop.name.trim(),
          phone: stop.phone.trim(),
          address: geo.routeAddress,
          lat: geo.lat,
          lng: geo.lng,
        };
      }),
    );

    return {
      pickup: {
        name: pickup.name.trim(),
        phone: pickup.phone.trim(),
        address: pickupGeo.routeAddress,
        lat: pickupGeo.lat,
        lng: pickupGeo.lng,
      },
      dropoffs: geocodedDropoffs,
    };
  }

  const handleGetQuote = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      const validationError = validateLalamoveContacts();
      if (validationError) {
        if (!silent) notifyFormError(validationError, 'Check pickup and dropoff');
        return;
      }

      if (silent) {
        setQuoteSilentRefreshing(true);
        setQuoteRefreshError(null);
      } else {
        setQuoteLoading(true);
        setQuoteRefreshError(null);
      }

      try {
        const geocoded = geocodedStops ?? (await buildGeocodedStops());
        const quote = await fetchMultiStopDeliveryQuote({
          pickup: geocoded.pickup,
          dropoffs: geocoded.dropoffs,
          vehicleType,
          specialRequests,
        });
        setGeocodedStops(geocoded);
        setQuotePreview(quote);
      } catch (err) {
        if (!silent) {
          setQuotePreview(null);
          setGeocodedStops(null);
          notifyFormError(
            err instanceof Error ? err.message : 'Could not fetch Lalamove quote.',
            'Quote failed',
          );
        } else {
          setQuoteRefreshError(
            err instanceof Error ? err.message : 'Could not update quote for additional services.',
          );
        }
      } finally {
        if (silent) setQuoteSilentRefreshing(false);
        else setQuoteLoading(false);
      }
    },
    [geocodedStops, vehicleType, specialRequests, notifyFormError, pickup, dropoffs],
  );

  useEffect(() => {
    if (step !== 3 || fulfillmentType !== 'lalamove') {
      prevSpecialRequestsRef.current = specialRequestsSignature;
      return;
    }

    const prev = prevSpecialRequestsRef.current;
    prevSpecialRequestsRef.current = specialRequestsSignature;
    if (prev === specialRequestsSignature) return;
    if (!geocodedStops && !quotePreview) return;

    const timer = window.setTimeout(() => {
      void handleGetQuote({ silent: true });
    }, 400);

    return () => window.clearTimeout(timer);
  }, [
    specialRequestsSignature,
    step,
    fulfillmentType,
    geocodedStops,
    quotePreview,
    handleGetQuote,
  ]);

  async function handleConfirm() {
    const step2Error = validateStep2();
    if (step2Error) {
      notifyFormError(step2Error, 'Check dropoff details');
      return;
    }

    if (fulfillmentType === 'internal' && internalType === 'sales' && !internalAssigneeName.trim()) {
      notifyFormError('Select a sales account from User Management.');
      return;
    }

    if (!requestedBy.trim()) {
      notifyFormError('Select who requested this delivery.');
      return;
    }
    if (!issueCausedBy.trim()) {
      notifyFormError('Select who/what caused the issue.');
      return;
    }

    const lalamoveValidationError = validateLalamoveContacts();
    if (lalamoveValidationError) {
      notifyFormError(lalamoveValidationError, 'Check pickup and dropoff');
      return;
    }

    if (fulfillmentType === 'lalamove') {
      if (!quotePreview || !geocodedStops || isDeliveryQuotationExpired(quotePreview)) {
        notifyFormError('Quote expired or missing. Get a new quote.', 'Quote expired');
        setQuotePreview(null);
        setGeocodedStops(null);
        return;
      }
    }

    setSaving(true);
    try {
      const geocoded = geocodedStops ?? (await buildGeocodedStops());
      const confirmInput = buildConfirmInput();
      const saved = reorderFromId
        ? await reorderCancelledDelivery(reorderFromId, confirmInput, geocoded, {
            existingQuotation: fulfillmentType === 'lalamove' ? quotePreview ?? undefined : undefined,
          })
        : await confirmMultiStopDelivery(confirmInput, geocoded, {
            existingQuotation: fulfillmentType === 'lalamove' ? quotePreview ?? undefined : undefined,
          });

      navigate(`${listBase}/${saved.id}`);
    } catch (err) {
      notifyFormError(
        err instanceof Error ? err.message : 'Could not save delivery.',
        'Could not save delivery',
      );
    } finally {
      setSaving(false);
    }
  }

  function goNext() {
    if (step === 1) {
      const err = validateStep1();
      if (err) {
        notifyFormError(err, 'Check pickup details');
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      const err = validateStep2();
      if (err) {
        notifyFormError(err, 'Check dropoff details');
        return;
      }
      setStep(3);
      prefillDriverNotes();
      if (fulfillmentType === 'lalamove') {
        void handleGetQuote();
      }
    }
  }

  function goBack() {
    if (step === 2) setStep(1);
    if (step === 3) setStep(2);
  }

  return (
    <div className="delivery-page delivery-page--form delivery-page--floating">
      <Link to={listBase} className="btn-secondary delivery-page__back">
        Back to list
      </Link>

      {reorderFromId ? (
        <p className="dem-floating-page-lead">
          Update the quote and confirm to place a new Lalamove order on the same list record. Edit
          pickup or dropoff only if something changed.
        </p>
      ) : null}

      {reorderPrefillLoading ? (
        <div className="alert alert-info">Loading cancelled arrangement…</div>
      ) : null}

      <nav className="delivery-wizard-steps" aria-label="Delivery arrangement steps">
        {WIZARD_STEPS.map((item, index) => {
          const status = step === item.step ? 'current' : step > item.step ? 'done' : 'upcoming';
          return (
            <div
              key={item.step}
              className={`delivery-wizard-steps__item delivery-wizard-steps__item--${status}`}
            >
              <span className="delivery-wizard-steps__index" aria-hidden="true">
                {status === 'done' ? '✓' : item.step}
              </span>
              <span className="delivery-wizard-steps__copy">
                <span className="delivery-wizard-steps__label">{item.label}</span>
                <span className="delivery-wizard-steps__caption">{item.caption}</span>
              </span>
              {index < WIZARD_STEPS.length - 1 ? (
                <span className="delivery-wizard-steps__connector" aria-hidden="true" />
              ) : null}
            </div>
          );
        })}
      </nav>

      <div className="delivery-form-layout">
        <div className="delivery-form-layout__main">
          <div className="card delivery-form-card">
            {step === 1 ? (
              <>
                <DeliveryFormCollapse
                  title="Arrangement"
                  open={arrangementOpen}
                  onToggle={() => setArrangementOpen((v) => !v)}
                >
                  <label className="field">
                    <span className="field-label">Arrangement type</span>
                    <select
                      className="field-input"
                      value={fulfillmentType}
                      onChange={(e) => {
                        const next = e.target.value as DeliveryFulfillmentType;
                        setFulfillmentType(next);
                        if (next === 'internal') {
                          setPickup((prev) => ({ ...prev, name: '', phone: '' }));
                        }
                      }}
                    >
                      <option value="lalamove">Lalamove delivery</option>
                      <option value="internal">Internal delivery</option>
                    </select>
                  </label>

                  {fulfillmentType === 'internal' ? (
                    <div className="delivery-form-card__section--nested">
                      <h3 className="delivery-form-card__subtitle">Internal assignee</h3>
                      <div
                        className={
                          internalType === 'sales'
                            ? 'delivery-form-card__assignee-row'
                            : undefined
                        }
                      >
                        <label className="field">
                          <span className="field-label">Delivered by</span>
                          <select
                            className="field-input"
                            value={internalType}
                            onChange={(e) => {
                              setInternalType(e.target.value as InternalAssigneeType);
                              setInternalAssigneeName('');
                            }}
                          >
                            <option value="driver">Driver</option>
                            <option value="sales">Sales</option>
                          </select>
                        </label>
                        {internalType === 'sales' ? (
                          <label className="field">
                            <span className="field-label">Sales account *</span>
                            <DemTypeahead
                              value={internalAssigneeName}
                              onChange={setInternalAssigneeName}
                              options={salesOptions}
                              placeholder="Search by name or email address"
                              inputClassName="field-input"
                            />
                          </label>
                        ) : (
                          <p className="delivery-form-card__hint">
                            Assigned as <strong>Driver</strong>. No individual name is required.
                          </p>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {fulfillmentType === 'lalamove' ? (
                    <div className="delivery-form-card__order-load">
                      <h3 className="delivery-form-card__subtitle">Load existing Lalamove order</h3>
                      <p className="delivery-form-card__hint">
                        Paste a <strong>Lalamove order ID</strong> or customer{' '}
                        <strong>tracking link</strong>. <strong>Load</strong> fills pickup and dropoff
                        from Lalamove; <strong>View</strong> is read-only.
                      </p>
                      <div className="delivery-form-card__order-load-row">
                        <label className="field delivery-form-card__order-load-field">
                          <span className="field-label">Lalamove order ID / tracking link</span>
                          <input
                            className="field-input"
                            value={orderIdInput}
                            onChange={(e) => setOrderIdInput(e.target.value)}
                            placeholder="Order ID or https://… tracking link"
                          />
                        </label>
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          disabled={orderLoadBusy || orderViewBusy}
                          onClick={() => void handleLoadLalamoveOrder()}
                        >
                          {orderLoadBusy ? 'Loading…' : 'Load'}
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={orderLoadBusy || orderViewBusy}
                          onClick={() => void handleViewLalamoveOrder()}
                        >
                          {orderViewBusy ? 'Loading…' : 'View'}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </DeliveryFormCollapse>

                <DeliveryFormCollapse
                  title="Attribution"
                  open={attributionOpen}
                  onToggle={() => setAttributionOpen((v) => !v)}
                >
                  <label className="field">
                    <span className="field-label">Requested by *</span>
                    <DemTypeahead
                      value={requestedBy}
                      onChange={setRequestedBy}
                      options={salesOptions}
                      placeholder="Search by name or email address"
                      inputClassName="field-input"
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Issue caused by *</span>
                    <DemTypeahead
                      value={issueCausedBy}
                      onChange={setIssueCausedBy}
                      options={issueCausedByOptions}
                      placeholder="Warehouse, Customer, Purchasing, or search a user"
                      inputClassName="field-input"
                    />
                  </label>
                </DeliveryFormCollapse>

                <DeliveryFormCollapse
                  title="Route & pickup"
                  open={routeOpen}
                  onToggle={() => setRouteOpen((v) => !v)}
                >
                  <LalamoveVehiclePicker
                    options={vehicleOptions}
                    value={vehicleType}
                    onChange={setVehicleType}
                    disabled={vehicleOptionsLoading}
                    loading={vehicleOptionsLoading}
                  />

                  <fieldset className="delivery-form-card__stop">
                    <legend>Pickup</legend>
                    <div className="delivery-form-card__warehouse-row">
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={warehouses.length === 0}
                        onClick={() => setWarehouseModalOpen(true)}
                      >
                        Choose warehouse address
                      </button>
                      {warehouses.length === 0 ? (
                        <span className="delivery-form-card__hint">
                          Add addresses in <Link to={settingsPath}>Settings → Deliveries</Link>.
                        </span>
                      ) : null}
                    </div>
                    <label className="field">
                      <span className="field-label">Address *</span>
                      <input
                        className="field-input"
                        value={pickup.address}
                        onChange={(e) => setPickup({ ...pickup, address: e.target.value })}
                        placeholder="Enter street address"
                      />
                    </label>
                    <div className="delivery-form-card__address-grid">
                      <label className="field">
                        <span className="field-label">Unit no.</span>
                        <input
                          className="field-input"
                          value={pickup.unitNo}
                          onChange={(e) => setPickup({ ...pickup, unitNo: e.target.value })}
                          placeholder="Enter unit number (optional)"
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">Building</span>
                        <input
                          className="field-input"
                          value={pickup.building}
                          onChange={(e) => setPickup({ ...pickup, building: e.target.value })}
                          placeholder="Enter building name (optional)"
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">Postal code *</span>
                        <input
                          className="field-input"
                          value={pickup.postal}
                          onChange={(e) => void applyRoutePostalToPickup(e.target.value)}
                          placeholder="Enter 6-digit postal code"
                          inputMode="numeric"
                        />
                      </label>
                    </div>
                    {pickup.address.trim() || pickup.postal.trim() ? (
                      <p className="delivery-form-card__hint">
                        Combined address: <strong>{composedAddress(pickup) || '—'}</strong>
                      </p>
                    ) : null}
                    {fulfillmentType === 'lalamove' ? (
                      <>
                        <p className="delivery-form-card__hint">
                          <strong>Contact</strong> — shown to the driver
                        </p>
                        <label className="field">
                          <span className="field-label">Name *</span>
                          <input
                            className="field-input"
                            value={pickup.name}
                            onChange={(e) => setPickup({ ...pickup, name: e.target.value })}
                            placeholder="Enter pickup contact name"
                          />
                        </label>
                        <label className="field">
                          <span className="field-label">Phone *</span>
                          <IntlPhoneField
                            value={pickup.phone}
                            onChange={(phone) => setPickup({ ...pickup, phone })}
                          />
                          <p className="delivery-form-card__hint">
                            Must differ from all dropoff phone numbers.
                          </p>
                        </label>
                      </>
                    ) : null}
                  </fieldset>
                </DeliveryFormCollapse>
              </>
            ) : null}

            {step === 2 ? (
              <div className="delivery-form-card__section">
                <h2 className="delivery-form-card__title">Dropoff stops</h2>

                <div className="delivery-form-card__dropoffs">
                  {dropoffs.map((stop, index) => (
                    <DeliveryFormCollapse
                      key={stop.key}
                      title={`Dropoff ${index + 1}`}
                      open={!closedDropoffKeys.has(stop.key)}
                      onToggle={() =>
                        setClosedDropoffKeys((prev) => {
                          const next = new Set(prev);
                          if (next.has(stop.key)) next.delete(stop.key);
                          else next.add(stop.key);
                          return next;
                        })
                      }
                    >
                      {dropoffs.length > 1 ? (
                        <div className="delivery-form-card__stop-actions">
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => {
                              setDropoffs((prev) => prev.filter((_, i) => i !== index));
                              setClosedDropoffKeys((prev) => {
                                const next = new Set(prev);
                                next.delete(stop.key);
                                return next;
                              });
                            }}
                          >
                            Remove stop
                          </button>
                        </div>
                      ) : null}

                      <label className="field">
                        <span className="field-label">Account *</span>
                        <CffAsyncTypeahead
                          value={stop.demCustomerCode}
                          displayLabel={
                            stop.demCustomerName.trim() && stop.demCustomerCode.trim()
                              ? `${stop.demCustomerName.trim()} (${stop.demCustomerCode.trim()})`
                              : undefined
                          }
                          onChange={(code) =>
                            updateDropoff(index, { demCustomerCode: code, demCustomerName: '' })
                          }
                          onSelect={(option) => void applyDemCustomer(index, option.value, option.meta)}
                          search={searchDemCustomers}
                          placeholder="Search by account code or name"
                        />
                      </label>

                      {!stop.crmMatched && stop.demCustomerCode ? (
                        <p className="delivery-form-card__hint delivery-form-card__hint--warn">
                          {crmMatchWarning(demMainCustomerCode(stop.demCustomerCode))}
                        </p>
                      ) : null}

                      <label className="field">
                        <span className="field-label">Address *</span>
                        <input
                          className="field-input"
                          value={stop.address}
                          onChange={(e) => updateDropoff(index, { address: e.target.value })}
                          placeholder="Enter street address"
                        />
                      </label>
                      <div className="delivery-form-card__address-grid">
                        <label className="field">
                          <span className="field-label">Unit no.</span>
                          <input
                            className="field-input"
                            value={stop.unitNo}
                            onChange={(e) => updateDropoff(index, { unitNo: e.target.value })}
                            placeholder="Enter unit number (optional)"
                          />
                        </label>
                        <label className="field">
                          <span className="field-label">Building</span>
                          <input
                            className="field-input"
                            value={stop.building}
                            onChange={(e) => updateDropoff(index, { building: e.target.value })}
                            placeholder="Enter building name (optional)"
                          />
                        </label>
                        <label className="field">
                          <span className="field-label">Postal code *</span>
                          <input
                            className="field-input"
                            value={stop.postal}
                            onChange={(e) => void applyRoutePostalToDropoff(index, e.target.value)}
                            placeholder="Enter 6-digit postal code"
                            inputMode="numeric"
                          />
                        </label>
                      </div>
                      {stop.address.trim() || stop.postal.trim() ? (
                        <p className="delivery-form-card__hint">
                          Combined address: <strong>{composedAddress(stop) || '—'}</strong>
                        </p>
                      ) : null}

                      <label className="field">
                        <span className="field-label">
                          Name{fulfillmentType === 'lalamove' ? ' *' : ''}
                        </span>
                        <input
                          className="field-input"
                          value={stop.name}
                          onChange={(e) => updateDropoff(index, { name: e.target.value })}
                          placeholder="Enter recipient name"
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">
                          Phone{fulfillmentType === 'lalamove' ? ' *' : ''}
                        </span>
                        <IntlPhoneField
                          value={stop.phone}
                          onChange={(phone) => updateDropoff(index, { phone })}
                        />
                      </label>

                      <label className="field">
                        <span className="field-label">Reason *</span>
                        <input
                          className="field-input"
                          value={stop.reason}
                          onChange={(e) => updateDropoff(index, { reason: e.target.value })}
                          placeholder="Enter reason for this delivery"
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">Document no.</span>
                        <input
                          className="field-input"
                          value={stop.documentNo}
                          onChange={(e) => updateDropoff(index, { documentNo: e.target.value })}
                          placeholder="Enter document or invoice number"
                        />
                      </label>
                    </DeliveryFormCollapse>
                  ))}
                </div>

                <button
                  type="button"
                  className="btn-secondary btn-sm delivery-form-card__add-stop"
                  onClick={() => setDropoffs((prev) => [...prev, newDropoffStop()])}
                >
                  Add stop
                </button>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="delivery-form-card__section">
                <h2 className="delivery-form-card__title">Review & confirm</h2>
                <p className="delivery-form-card__hint">
                  Check the details below
                  {fulfillmentType === 'lalamove'
                    ? ', add notes for the driver, choose additional services, then confirm the order.'
                    : ', then save the internal delivery.'}
                </p>

                <div className="delivery-review">
                  <section className="delivery-review__block">
                    <h3 className="delivery-review__heading">Arrangement</h3>
                    <dl className="delivery-review__dl">
                      <div>
                        <dt>Type</dt>
                        <dd>
                          {fulfillmentType === 'lalamove' ? 'Lalamove delivery' : 'Internal delivery'}
                        </dd>
                      </div>
                      <div>
                        <dt>Vehicle</dt>
                        <dd>{vehicleLabel}</dd>
                      </div>
                      {fulfillmentType === 'internal' ? (
                        <div>
                          <dt>Assignee</dt>
                          <dd>
                            {internalType === 'driver'
                              ? 'Driver'
                              : internalAssigneeName || '—'}
                          </dd>
                        </div>
                      ) : null}
                      <div>
                        <dt>Creator</dt>
                        <dd>{creator}</dd>
                      </div>
                      <div>
                        <dt>Requested by</dt>
                        <dd>{requestedBy || '—'}</dd>
                      </div>
                      <div>
                        <dt>Issue caused by</dt>
                        <dd>{issueCausedBy || '—'}</dd>
                      </div>
                    </dl>
                  </section>

                  <section className="delivery-review__block">
                    <h3 className="delivery-review__heading">Pickup</h3>
                    <dl className="delivery-review__dl">
                      <div>
                        <dt>Address</dt>
                        <dd>{composedAddress(pickup) || '—'}</dd>
                      </div>
                      <div>
                        <dt>Contact</dt>
                        <dd>
                          {pickup.name || '—'}
                          {pickup.phone ? ` · ${pickup.phone}` : ''}
                        </dd>
                      </div>
                    </dl>
                  </section>

                  {dropoffs.map((stop, index) => (
                    <section key={stop.key} className="delivery-review__block">
                      <h3 className="delivery-review__heading">Dropoff {index + 1}</h3>
                      <dl className="delivery-review__dl">
                        <div>
                          <dt>Account</dt>
                          <dd>
                            {stop.demCustomerName || stop.demCustomerCode || '—'}
                            {stop.demCustomerCode ? ` (${stop.demCustomerCode})` : ''}
                          </dd>
                        </div>
                        <div>
                          <dt>Address</dt>
                          <dd>{composedAddress(stop) || '—'}</dd>
                        </div>
                        <div>
                          <dt>Contact</dt>
                          <dd>
                            {stop.name || '—'}
                            {stop.phone ? ` · ${stop.phone}` : ''}
                          </dd>
                        </div>
                        <div>
                          <dt>Reason</dt>
                          <dd>{stop.reason || '—'}</dd>
                        </div>
                        <div>
                          <dt>Document no.</dt>
                          <dd>{stop.documentNo || '—'}</dd>
                        </div>
                      </dl>
                    </section>
                  ))}
                </div>

                {fulfillmentType === 'lalamove' ? (
                  <div className="delivery-review__notes">
                    <h3 className="delivery-review__heading">Notes to drivers</h3>
                    <p className="delivery-form-card__hint">
                      Sent to Lalamove as remarks for the driver. Edit{' '}
                      <code>[quantity]</code> manually before confirming.
                    </p>
                    {dropoffs.map((stop, index) => (
                      <label key={stop.key} className="field">
                        <span className="field-label">
                          {dropoffs.length > 1 ? `Dropoff ${index + 1} notes` : 'Notes to drivers'}
                        </span>
                        <textarea
                          className="field-input"
                          rows={4}
                          value={stop.notesToDriver}
                          onChange={(e) => updateDropoff(index, { notesToDriver: e.target.value })}
                          placeholder="Enter instructions for the driver"
                        />
                      </label>
                    ))}
                  </div>
                ) : null}

                {fulfillmentType === 'lalamove' ? (
                  <div className="delivery-review__notes delivery-review__services">
                    <h3 className="delivery-review__heading">Additional services</h3>
                    <p className="delivery-form-card__hint">
                      Options come from Lalamove and differ by vehicle (e.g. helper and secure zone
                      are usually on Van / Lorry only). The quote below updates automatically when you
                      change options.
                    </p>
                    <LalamoveSpecialRequestsPicker
                      options={availableSpecialRequests}
                      selected={specialRequests}
                      onChange={setSpecialRequests}
                      disabled={vehicleOptionsLoading || quoteLoading || saving}
                    />
                  </div>
                ) : null}

                {fulfillmentType === 'lalamove' ? (
                  <div className="delivery-form-card__quote delivery-review__quote">
                    <div className="delivery-review__quote-head">
                      <h3 className="delivery-review__heading">Lalamove quotation</h3>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={quoteLoading || quoteSilentRefreshing || saving}
                        onClick={() => void handleGetQuote()}
                      >
                        {quoteLoading || quoteSilentRefreshing
                          ? 'Getting quote…'
                          : hasValidQuote
                            ? 'Refresh quote'
                            : 'Get quote'}
                      </button>
                    </div>
                    {hasValidQuote && quotePreview ? (
                      <>
                        <p
                          className="delivery-form-card__quote-amount"
                          style={quoteSilentRefreshing ? { opacity: 0.65 } : undefined}
                        >
                          {formatDeliveryMoney(
                            quotePreview.referenceFeeAmount,
                            quotePreview.referenceFeeCurrency,
                          )}
                        </p>
                        <p className="delivery-form-card__hint">
                          Official Lalamove quote
                          {quoteSilentRefreshing ? ' · updating…' : ''} · expires{' '}
                          {quotePreview.expiresAt
                            ? formatSingaporeDateTime(quotePreview.expiresAt)
                            : 'soon'}
                          .
                        </p>
                      </>
                    ) : (
                      <p className="delivery-form-card__hint">
                        {quoteLoading
                          ? 'Requesting an official Lalamove quote…'
                          : 'A valid quote is required before confirming the order.'}
                      </p>
                    )}
                    {quoteRefreshError ? (
                      <p className="delivery-form-card__hint delivery-form-card__hint--warn">
                        {quoteRefreshError} Use <strong>Refresh quote</strong> to try again.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <p className="delivery-form-card__hint">Creator: {creator}</p>
              </div>
            ) : null}

            <div className="delivery-form-card__actions delivery-wizard-actions">
              {step > 1 ? (
                <button type="button" className="btn-secondary" disabled={saving || quoteLoading || quoteSilentRefreshing} onClick={goBack}>
                  Back
                </button>
              ) : (
                <Link to={listBase} className="btn-secondary">
                  Cancel
                </Link>
              )}

              {step < 3 ? (
                <button type="button" className="btn-primary" onClick={goNext}>
                  Next
                </button>
              ) : fulfillmentType === 'lalamove' ? (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={saving || quoteLoading || quoteSilentRefreshing || !hasValidQuote}
                  onClick={() => void handleConfirm()}
                >
                  {saving
                    ? 'Placing order…'
                    : reorderFromId
                      ? hasValidQuote && quotePreview
                        ? `Confirm reorder · ${formatDeliveryMoney(
                            quotePreview.referenceFeeAmount,
                            quotePreview.referenceFeeCurrency,
                          )}`
                        : 'Confirm reorder'
                      : hasValidQuote && quotePreview
                        ? `Confirm order · ${formatDeliveryMoney(
                            quotePreview.referenceFeeAmount,
                            quotePreview.referenceFeeCurrency,
                          )}`
                        : 'Confirm order'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={saving}
                  onClick={() => void handleConfirm()}
                >
                  {saving ? 'Saving…' : 'Save internal delivery'}
                </button>
              )}
            </div>
          </div>
        </div>

        <aside className="delivery-form-layout__aside">
          <div className="card delivery-aside-card delivery-aside-card--map">
            <div className="delivery-aside-card__head">
              <span className="delivery-aside-card__eyebrow">Route preview</span>
              <h2 className="delivery-aside-card__title">Map & coordinates</h2>
            </div>
            <DeliveryRouteMap stops={mapStops} />
          </div>

          <div className="card delivery-aside-card delivery-aside-card--summary">
            <div className="delivery-aside-card__head">
              <span className="delivery-aside-card__eyebrow">Trip summary</span>
              <h2 className="delivery-aside-card__title">
                {step === 1 ? 'Pickup details' : step === 2 ? 'Dropoff details' : 'Before you confirm'}
              </h2>
            </div>
            {fulfillmentType === 'lalamove' && hasValidQuote && quotePreview ? (
              <div className="delivery-form-card__quote">
                <p className="delivery-aside-card__eyebrow">Official quote</p>
                <p className="delivery-form-card__quote-amount">
                  {formatDeliveryMoney(
                    quotePreview.referenceFeeAmount,
                    quotePreview.referenceFeeCurrency,
                  )}
                </p>
                <p className="delivery-aside-card__hint">
                  Expires{' '}
                  {quotePreview.expiresAt ? formatSingaporeDateTime(quotePreview.expiresAt) : 'soon'}
                </p>
              </div>
            ) : null}
            <dl className="delivery-aside-summary">
              <div className="delivery-aside-summary__row">
                <dt>Step</dt>
                <dd>
                  {step} of 3
                </dd>
              </div>
              <div className="delivery-aside-summary__row">
                <dt>Type</dt>
                <dd>{fulfillmentType === 'lalamove' ? 'Lalamove delivery' : 'Internal delivery'}</dd>
              </div>
              <div className="delivery-aside-summary__row">
                <dt>Vehicle</dt>
                <dd>{vehicleLabel}</dd>
              </div>
              <div className="delivery-aside-summary__row">
                <dt>Dropoff stops</dt>
                <dd>{dropoffs.length}</dd>
              </div>
              <div className="delivery-aside-summary__row">
                <dt>Map stops</dt>
                <dd>{mapStops.length}</dd>
              </div>
              {crmUnmatchedCount > 0 ? (
                <div className="delivery-aside-summary__row delivery-aside-summary__row--warn">
                  <dt>CRM</dt>
                  <dd>
                    {crmUnmatchedCount} stop{crmUnmatchedCount === 1 ? '' : 's'} without CRM match
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        </aside>
      </div>

      <Modal
        title="Choose warehouse address"
        open={warehouseModalOpen}
        onClose={() => setWarehouseModalOpen(false)}
        wide
      >
        <p className="delivery-form-card__hint" style={{ marginTop: 0 }}>
          Select a saved warehouse to fill pickup location and contact details.
        </p>
        <div className="delivery-form-card__warehouse-menu">
          {warehouses.map((warehouse) => (
            <button
              key={warehouse.id}
              type="button"
              className="delivery-form-card__warehouse-option"
              onClick={() => applyWarehouse(warehouse)}
            >
              <strong>{warehouse.label}</strong>
              <span>{composeDeliveryAddress(warehouse) || warehouse.address}</span>
              {warehouse.name || warehouse.phone ? (
                <span>{[warehouse.name, warehouse.phone].filter(Boolean).join(' · ')}</span>
              ) : null}
            </button>
          ))}
        </div>
      </Modal>

      <LalamoveOrderViewModal
        order={viewLalamoveOrder}
        open={orderViewOpen}
        onClose={() => setOrderViewOpen(false)}
      />
    </div>
  );
}
